import { useCallback, useEffect, useState } from 'react';
import { SearchInput } from '@/shared/ui/SearchInput';
import { BadgeSyncPendente } from '@/shared/ui/BadgeSyncPendente';
import { pdvApi, type VendaDetalhe, type VendaResumo } from '../api/pdvApi';
import { formatarDataHora, formatarDinheiro, textoDoErro } from '../formato';

type Props = {
  onImprimir: (venda: VendaDetalhe) => void;
  onDevolver: (vendaId: string) => void;
  /** Devolver exige caixa aberto — ela entra no caixa de hoje. */
  podeDevolver: boolean;
};

/**
 * Histórico do PDV. A busca aceita o número canônico e o provisório: o
 * cupom impresso offline saiu com o provisório, e é o papel que o cliente
 * traz de volta quando quer trocar a peça.
 */
export function PdvHistoricoVendas({ onImprimir, onDevolver, podeDevolver }: Props) {
  const [vendas, setVendas] = useState<VendaResumo[]>([]);
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async (termo: string) => {
    setCarregando(true);
    try {
      setVendas(await pdvApi.listarVendas(termo ? { busca: termo } : {}));
      setErro(null);
    } catch (e) {
      setErro(textoDoErro(e));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => void carregar(busca.trim()), 250);
    return () => clearTimeout(t);
  }, [busca, carregar]);

  async function abrirCupom(id: string) {
    try {
      onImprimir(await pdvApi.obterVenda(id));
    } catch (e) {
      setErro(textoDoErro(e));
    }
  }

  return (
    <div className="pdv-historico">
      <div className="pdv-historico__filtros">
        <SearchInput
          valor={busca}
          onChange={setBusca}
          placeholder="Número da venda (do cupom) ou parte dele"
        />
      </div>

      {erro && (
        <p className="pdv-aviso pdv-aviso--erro" role="alert">
          {erro}
        </p>
      )}

      {carregando && vendas.length === 0 && <p className="pdv-centro--vazio">Carregando…</p>}

      {!carregando && vendas.length === 0 && (
        <div className="pdv-carrinho-vazio">
          <p>Nenhuma venda encontrada.</p>
          {busca && <p className="pdv-carrinho-vazio__dica">Tente outro número.</p>}
        </div>
      )}

      {vendas.length > 0 && (
        <ul className="pdv-historico__lista">
          {vendas.map((v) => (
            <li key={v.id} className="pdv-historico__item">
              <div className="pdv-historico__identidade">
                <span className="pdv-historico__numero">
                  {v.numero}
                  {v.vendaOrigemId && (
                    <span className="pdv-historico__marca">
                      devolução de {v.vendaOrigemNumero ?? '—'}
                    </span>
                  )}
                  {v.fiado && <span className="pdv-historico__marca">fiado</span>}
                  {v.syncPendente && <BadgeSyncPendente pendente rotulo="Número provisório" />}
                </span>
                <span className="pdv-historico__cliente">{v.clienteNome}</span>
              </div>
              <div className="pdv-historico__meta">
                <span>{formatarDataHora(v.data)}</span>
                <span>
                  {v.quantidadeItens} item(s) · {v.colaboradorNome ?? '—'}
                </span>
              </div>
              <strong className="pdv-historico__valor">{formatarDinheiro(v.valorTotal)}</strong>
              <div className="pdv-historico__acoes">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => void abrirCupom(v.id)}
                >
                  Ver cupom
                </button>
                {/* Devolução só faz sentido sobre uma venda, não sobre
                    outra devolução. */}
                {!v.vendaOrigemId && (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => onDevolver(v.id)}
                    disabled={!podeDevolver}
                    title={podeDevolver ? undefined : 'Abra um caixa para registrar devoluções.'}
                  >
                    Devolver
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
